/**
 * ConversionLedger — the Conversion section of the Reports page (founder
 * redesign 2026-07-14; formerly the Workspace Conversion view's funnel).
 *
 * Two SAME-STYLED headline rates: Full Funnel Conversion Rate (converted / all
 * intakes) and Qualified Conversion Rate — the founder's operating definition
 * (2026-07-15): "paperwork received ÷ paperwork sent." Paperwork SENT = the intake reached
 * the paperwork_sent stage (arriving there means paperwork went out, label
 * "Paperwork Pending"); paperwork RECEIVED = the intake advanced past
 * paperwork to Client Setup or beyond — the server's paperwork gate
 * (api/_shared/repos/workItems.ts, "Client-creation paperwork gate") requires
 * received paperwork before entering client_setup, so reached(client_setup)
 * is exactly that population. Both are the ledger's existing reached-≥-stage
 * counts (reachedAtLeast / ledgerRows) — no backend change.
 *
 * The reconciliation strip ("N intakes = W won + L lost + D still deciding"
 * — every chip clickable), and the stage LEDGER: one row per stage with
 * ADDITIVE accounting. Each row reads
 * "STAGE · N arrived", then "A moved on (x%) · L lost here (y%) · D still
 * deciding" (+ "W working here (won)" whenever a won client sits here). A
 * converted intake is positioned at LEAST at the convert line (the backend
 * clamps a won-then-dragged-back client there), so "working" only ever appears
 * at/past the convert line and a won person is never hidden below it. The
 * composition bar's WIDTH is proportional to arrived/total (the funnel
 * narrowing stays visible) and its SEGMENTS are proportional within arrived.
 * INVARIANT (pinned in tests): arrived = moved on + lost here + still deciding
 * + working here, per row; column sums equal the strip.
 *
 * Register law: forest = moved on; pepper = genuine loss ONLY; gold = still
 * deciding; affirm reserved for won. A legend line states it at the bottom.
 *
 * ANOMALY FLAG (the past-the-line loss class): a loss or open-unconverted case
 * at/past the convert line renders a pepper flag line on that row, wired to
 * the drill (furthest:<stage>:unconverted). NEVER hidden.
 *
 * AUDIT: every number opens the list of the actual people behind it (the
 * drill panel); the panel's count is server-guaranteed equal to the clicked
 * number (the trust contract), and each listed person opens the intake drawer
 * (the onOpen prop — capability-driven at the host: Reports passes it for
 * view_workspace holders).
 *
 * "Why we lose them": display labels are NORMALIZED (import-artifact variants
 * like "Closed - No Response" merge into "No Response" — mergeCloseReasons);
 * each reason row drills reason:<label>. The stored data is never rewritten.
 *
 * Self-contained (owns its period + fetch); brand tokens only.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { OverlayPortal } from '@/components/primitives/OverlayPortal';
import { useTenant } from '@/lib/auth';
import { useConversion, useConversionDrill } from '@/lib/workspace';
import { CLIENT_SETUP_STAGE_KEY, ASSIGNED_STAGE_KEY, PAPERWORK_SENT_STAGE_KEY } from '@/lib/workspaceStageMove';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SkeletonRow } from '@/components/Skeleton';
import type { ConversionDrillRowWire } from '@/lib/api';
import {
  customRange,
  formatPct,
  ledgerRows,
  mergeCloseReasons,
  presetRange,
  type ConversionPreset,
  type DateRange,
  type LedgerRow,
} from './conversionFormat';
import { DateInput } from '@/components/primitives/DateInput';

type PeriodMode = ConversionPreset | 'custom';

/** The drilled segment: the wire selector + a human label for the panel header. */
interface DrillTarget {
  segment: string;
  label: string;
}

export function ConversionLedger({ onOpen }: { onOpen?: (id: string) => void }): ReactNode {
  const { tenantSlug } = useTenant();
  // `now` fixed at mount so the preset range (and the query key) is stable across renders.
  const now = useMemo(() => new Date(), []);
  const [mode, setMode] = useState<PeriodMode>('this_month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [drill, setDrill] = useState<DrillTarget | null>(null);

  const range: DateRange | null = useMemo(() => {
    if (mode === 'custom') return customRange(fromDate, toDate);
    return presetRange(mode, now);
  }, [mode, fromDate, toDate, now]);

  const { metrics, isLoading, isError, error, refetch } = useConversion(
    tenantSlug,
    range ?? { from: '', to: '' },
    { enabled: range !== null },
  );

  const PERIODS: { key: PeriodMode; label: string }[] = [
    { key: 'this_month', label: 'This month' },
    { key: 'this_quarter', label: 'This quarter' },
    { key: 'six_months', label: '6 months' },
    { key: 'all_time', label: 'All time' },
    { key: 'custom', label: 'Custom' },
  ];

  // Changing the period closes any open drill (its counts no longer apply).
  function changeMode(next: PeriodMode): void {
    setMode(next);
    setDrill(null);
  }

  return (
    <div data-testid="conversion-ledger-section">
      {/* Period selector */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div role="group" aria-label="Period" className="flex flex-wrap gap-1 bg-paper border border-sage-300 rounded-base p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              data-testid={`conversion-period-${p.key}`}
              aria-pressed={mode === p.key}
              onClick={() => changeMode(p.key)}
              className={
                'px-3 py-1.5 text-[13px] font-semibold rounded-[7px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                (mode === p.key ? 'bg-sage-100 text-sage-800' : 'text-sage-400 hover:text-sage-800')
              }
            >
              {p.label}
            </button>
          ))}
        </div>
        {mode === 'custom' && (
          <div className="flex items-center gap-1.5 text-[13px] text-sage-400">
            <DateInput aria-label="From date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setDrill(null); }}
              data-testid="conversion-custom-from" size="compact" />
            <span>→</span>
            <DateInput aria-label="To date" value={toDate} onChange={(e) => { setToDate(e.target.value); setDrill(null); }}
              data-testid="conversion-custom-to" size="compact" />
          </div>
        )}
      </div>

      {mode === 'custom' && range === null ? (
        <p className="text-sm text-ink-soft px-2 py-4" data-testid="conversion-needs-dates">Pick a start and end date.</p>
      ) : isLoading ? (
        <div><SkeletonRow /><SkeletonRow /></div>
      ) : isError ? (
        <ErrorBanner error={error} onRetry={refetch} what="the ledger" />
      ) : !metrics || metrics.cohort_total === 0 ? (
        <p className="text-sm text-ink-soft px-2 py-4" data-testid="conversion-empty">Once you record intakes for these dates, you'll see them here.</p>
      ) : (
        <Metrics metrics={metrics} onDrill={setDrill} />
      )}

      {/* Drill panel — the actual people behind a clicked number. */}
      {drill && range && (
        <DrillPanel
          slug={tenantSlug}
          range={range}
          target={drill}
          onClose={() => setDrill(null)}
          onOpen={onOpen}
        />
      )}
    </div>
  );
}

/** A small register dot — the shared visual vocabulary between the reconciliation
 *  strip, the ledger segments, and the legend (register law: forest = moved on,
 *  pepper = loss ONLY, gold = deciding, affirm reserved for won). */
function Dot({ tone }: { tone: 'moved' | 'won' | 'loss' | 'deciding' | 'neutral' }): ReactNode {
  const cls =
    tone === 'moved' ? 'bg-sage-800'
    : tone === 'won' ? 'bg-affirm-deep'
    : tone === 'loss' ? 'bg-pepper'
    : tone === 'deciding' ? 'bg-warm-400'
    : 'bg-sage-300';
  return <span className={`inline-block w-1.5 h-1.5 rounded-pill flex-shrink-0 ${cls}`} aria-hidden="true" />;
}

function Metrics({
  metrics,
  onDrill,
}: {
  metrics: NonNullable<ReturnType<typeof useConversion>['metrics']>;
  onDrill: (t: DrillTarget) => void;
}): ReactNode {
  const rows = ledgerRows(metrics.funnel);
  const total = rows[0]?.arrived ?? metrics.cohort_total; // == cohort_total
  const convertSort = metrics.funnel.find((s) => s.key === CLIENT_SETUP_STAGE_KEY)?.sort_order ?? Infinity;
  const reasons = mergeCloseReasons(metrics.close_reasons);

  // Founder-definition conversion rate (2026-07-15): paperwork received ÷
  // paperwork sent. "Sent" = reached(paperwork_sent); "received" =
  // reached(client_setup) (the server's paperwork gate requires received
  // paperwork before an intake can enter client_setup — see the module
  // header). Both are the ledger's existing reached-≥-stage counts.
  const paperworkSent = rows.find((r) => r.key === PAPERWORK_SENT_STAGE_KEY)?.arrived ?? 0;
  const paperworkReceived = rows.find((r) => r.key === CLIENT_SETUP_STAGE_KEY)?.arrived ?? 0;

  return (
    <div className="space-y-6">
      {/* Two SAME-STYLED rates — Full Funnel + the founder-definition
          Conversion rate. Typography/color are identical (design law: visual
          difference only where it means something); the only difference is
          the label + ratio. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="conversion-headline">
        <RateCard
          testid="conversion-rate-fullfunnel"
          title="Full Funnel Conversion Rate"
          value={formatPct(metrics.conversion_rate)}
          caption={`converted ÷ all intakes · ${metrics.converted} of ${metrics.cohort_total}`}
        />
        <RateCard
          testid="conversion-rate-paperwork"
          title="Qualified Conversion Rate"
          // M=0 (no paperwork sent in the window) is an honest empty state —
          // never NaN or 0%; the en-dash matches the codebase's existing
          // no-data glyph (operatingDerive.ts, PartnerEngagementBlock.tsx).
          value={paperworkSent === 0 ? '–' : formatPct(paperworkReceived / paperworkSent)}
          caption={`paperwork received ÷ paperwork sent · ${paperworkReceived} of ${paperworkSent}`}
        />
      </div>

      {/* Reconciliation strip — every intake is counted exactly once. This is
          the identity the ledger decomposes; the founder can math it directly.
          Each population is a button that opens the list of those exact people. */}
      <div className="bg-paper rounded-card shadow-sh1 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px]" data-testid="conversion-reconciliation">
        <span className="tabular-nums font-semibold text-sage-800">{metrics.cohort_total} intakes</span>
        <span className="text-sage-400">=</span>
        <DrillChip
          testid="conversion-chip-converted"
          tone="won"
          count={metrics.converted}
          label="won"
          onClick={() => onDrill({ segment: 'converted', label: 'Won (converted)' })}
        />
        <span className="text-sage-300">+</span>
        <DrillChip
          testid="conversion-chip-closed"
          tone="loss"
          count={metrics.closed_unconverted}
          label="lost"
          onClick={() => onDrill({ segment: 'closed_unconverted', label: 'Lost (closed, unconverted)' })}
        />
        <span className="text-sage-300">+</span>
        <DrillChip
          testid="conversion-chip-inprogress"
          tone="deciding"
          count={metrics.in_progress}
          label="still deciding"
          onClick={() => onDrill({ segment: 'in_progress', label: 'Still deciding' })}
        />
      </div>

      {/* THE LEDGER — one row per stage, additive accounting. */}
      <div data-testid="conversion-ledger">
        <div className="text-xs uppercase tracking-wider font-bold text-sage-400 mb-2.5">Stage ledger · where everyone went</div>
        <div className="bg-paper rounded-card shadow-sh1 px-4 py-3.5 space-y-3">
          {rows.map((row, i) => {
            const isConvertLine = row.key === CLIENT_SETUP_STAGE_KEY;
            const isAssigned = row.key === ASSIGNED_STAGE_KEY && i === rows.length - 1;
            return (
              <div key={row.key}>
                {/* Convert line marker — sits ABOVE the stage where the client
                    record is minted. */}
                {isConvertLine && (
                  <div
                    data-testid="conversion-convert-line"
                    className="flex items-center gap-2 mb-3 mt-1"
                  >
                    <span className="h-px flex-1 bg-sage-300" aria-hidden="true" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-sage-700 bg-sage-50 rounded-pill px-2 py-0.5">
                      converts here · client record created
                    </span>
                    <span className="h-px flex-1 bg-sage-300" aria-hidden="true" />
                  </div>
                )}
                {isAssigned ? (
                  <AssignedBand row={row} onDrill={onDrill} />
                ) : (
                  <LedgerStageRow row={row} rows={rows} index={i} total={total} convertSort={convertSort} onDrill={onDrill} />
                )}
                {/* ANOMALY FLAG — losses / still-open cases at or past the
                    convert line are unusual (the past-the-line loss class) and
                    NEVER hidden. Applies to the Assigned band too. */}
                {row.sort_order >= convertSort && row.lost_here + row.deciding > 0 && (
                  <button
                    type="button"
                    data-testid="conversion-ledger-anomaly"
                    data-stage={row.key}
                    data-count={row.lost_here + row.deciding}
                    onClick={() =>
                      onDrill({
                        segment: `furthest:${row.key}:unconverted`,
                        label: `Past the convert line at ${row.label} (unusual)`,
                      })
                    }
                    className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-pepper rounded-tight px-1 py-0.5 hover:bg-pepper/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                  >
                    <Dot tone="loss" />
                    unusual: {row.lost_here + row.deciding} past the convert line · click to see who
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {/* Register legend — the color law, stated. */}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-sage-400 px-1" data-testid="conversion-ledger-legend">
          <span className="inline-flex items-center gap-1"><Dot tone="moved" />moved on</span>
          <span className="inline-flex items-center gap-1"><Dot tone="loss" />lost here</span>
          <span className="inline-flex items-center gap-1"><Dot tone="deciding" />still deciding</span>
          <span className="inline-flex items-center gap-1"><Dot tone="won" />working here (won)</span>
          <span>Every number opens the list of the actual people behind it.</span>
        </div>
      </div>

      {/* Why we lose them — merged display labels; each row drills its people. */}
      <div data-testid="conversion-dropoff">
        <div className="text-xs uppercase tracking-wider font-bold text-sage-400 mb-2.5">Why we lose them · {metrics.closed_unconverted} lost</div>
        {reasons.length === 0 ? (
          <p className="text-sm text-ink-soft px-1">No lost intakes in this period.</p>
        ) : (
          <div className="bg-paper rounded-card shadow-sh1 px-4 py-3.5 space-y-2">
            {reasons.map((r) => {
              const pct = metrics.closed_unconverted === 0 ? 0 : Math.round((r.count / metrics.closed_unconverted) * 100);
              return (
                <button
                  key={r.label}
                  type="button"
                  data-testid="conversion-dropoff-reason"
                  data-reason={r.label}
                  data-count={r.count}
                  onClick={() => onDrill({ segment: `reason:${r.label}`, label: `Lost: ${r.label}` })}
                  className="block w-full text-left rounded-tight px-1 py-0.5 -mx-1 hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                >
                  <div className="flex items-baseline gap-2 text-[13px]">
                    <span className="text-ink">{r.label}</span>
                    <span className="ml-auto tabular-nums text-sage-800 font-semibold">{r.count}</span>
                  </div>
                  <div className="mt-1 h-2 rounded-pill bg-sage-50 overflow-hidden">
                    <div className="h-full rounded-pill bg-pepper/70" style={{ width: `${pct}%` }} aria-hidden="true" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** One composition-bar segment; zero-width when the count is zero. */
function BarSegment({ tone, count, arrived }: { tone: 'moved' | 'won' | 'loss' | 'deciding'; count: number; arrived: number }): ReactNode {
  const cls =
    tone === 'moved' ? 'bg-sage-800'
    : tone === 'won' ? 'bg-affirm-deep/70'
    : tone === 'loss' ? 'bg-pepper'
    : 'bg-warm-400';
  const pct = arrived === 0 ? 0 : (count / arrived) * 100;
  return (
    <div
      data-testid={`conversion-bar-${tone}`}
      className={`h-full ${cls}`}
      style={{ width: `${pct}%`, minWidth: count > 0 ? '3px' : '0' }}
      aria-hidden="true"
    />
  );
}

/**
 * One ledger stage row: "STAGE · N arrived", the composition bar (row width ∝
 * arrived/total; segments ∝ within arrived), and the additive breakdown line —
 * every number a drill button.
 */
function LedgerStageRow({
  row,
  rows,
  index,
  total,
  convertSort,
  onDrill,
}: {
  row: LedgerRow;
  rows: LedgerRow[];
  index: number;
  total: number;
  convertSort: number;
  onDrill: (t: DrillTarget) => void;
}): ReactNode {
  const postConvert = row.sort_order >= convertSort;
  const nextKey = rows[index + 1]?.key ?? null;
  const widthPct = total === 0 ? 0 : Math.round((row.arrived / total) * 100);
  const pctOf = (n: number): string => (row.arrived === 0 ? '0%' : `${Math.round((n / row.arrived) * 100)}%`);

  return (
    <div data-testid="conversion-ledger-row" data-stage={row.key} data-region={postConvert ? 'post-convert' : 'pre-convert'}>
      {/* Header line: stage + N arrived (drills reached-≥ this stage). */}
      <button
        type="button"
        data-testid="conversion-ledger-arrived"
        data-arrived={row.arrived}
        onClick={() => onDrill({ segment: `reached:${row.key}`, label: `Arrived at ${row.label}` })}
        className="w-full text-left rounded-tight px-1 py-0.5 -mx-1 hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        <div className="flex items-baseline gap-2 text-[13px]">
          <span className="font-semibold text-ink">{row.label}</span>
          <span className="ml-auto tabular-nums text-sage-800 font-semibold">{row.arrived} arrived</span>
        </div>
        {/* Composition bar: WIDTH ∝ arrived/total (funnel narrowing stays
            visible); SEGMENTS ∝ within arrived. */}
        <div className="mt-1 h-2.5 rounded-pill bg-sage-50 overflow-hidden" style={{ width: `${widthPct}%`, minWidth: row.arrived > 0 ? '12px' : '0' }}>
          <div className="h-full flex">
            <BarSegment tone="moved" count={row.moved_on} arrived={row.arrived} />
            <BarSegment tone="won" count={row.working} arrived={row.arrived} />
            <BarSegment tone="deciding" count={row.deciding} arrived={row.arrived} />
            <BarSegment tone="loss" count={row.lost_here} arrived={row.arrived} />
          </div>
        </div>
      </button>
      {/* The additive breakdown — each population its own drill button. */}
      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] tabular-nums">
        {nextKey && (
          <BreakdownChip
            testid="conversion-ledger-moved"
            tone="moved"
            count={row.moved_on}
            text={`moved on (${pctOf(row.moved_on)})`}
            onClick={() => onDrill({ segment: `reached:${nextKey}`, label: `Moved past ${row.label}` })}
          />
        )}
        <BreakdownChip
          testid="conversion-ledger-lost"
          tone="loss"
          count={row.lost_here}
          text={`lost here (${pctOf(row.lost_here)})`}
          onClick={() => onDrill({ segment: `furthest:${row.key}:lost`, label: `Lost at ${row.label}` })}
        />
        <BreakdownChip
          testid="conversion-ledger-deciding"
          tone="deciding"
          count={row.deciding}
          text="still deciding"
          onClick={() => onDrill({ segment: `furthest:${row.key}:deciding`, label: `Still deciding at ${row.label}` })}
        />
        {/* Defense-in-depth: render the "working (won)" chip whenever there IS
            a won person here, not gated on position. The backend clamps a
            converted intake to at least the convert line, so row.working is
            non-zero only at/past the line anyway — but if that ever regressed,
            a won person must never be hidden (the audit promise). */}
        {row.working > 0 && (
          <BreakdownChip
            testid="conversion-ledger-working"
            tone="won"
            count={row.working}
            text="working here (won)"
            onClick={() => onDrill({ segment: `furthest:${row.key}:working`, label: `Won, working at ${row.label}` })}
          />
        )}
      </div>
    </div>
  );
}

/** One breakdown population chip: "N label", drillable. */
function BreakdownChip({
  testid,
  tone,
  count,
  text,
  onClick,
}: {
  testid: string;
  tone: 'moved' | 'won' | 'loss' | 'deciding';
  count: number;
  text: string;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      data-testid={testid}
      data-count={count}
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-tight px-1 py-0.5 -mx-0.5 text-sage-400 hover:bg-sage-50 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
    >
      <Dot tone={tone} />
      <span className="font-semibold text-ink">{count}</span>
      <span>{text}</span>
    </button>
  );
}

/**
 * The final "Assigned · won clients" affirm band — every case here is a won
 * client with a care manager (the affirm register is reserved for exactly
 * this). Clickable: drills the won-and-working population at Assigned. An
 * anomalous unconverted case at Assigned is flagged by the ledger's shared
 * anomaly line (rendered by the caller), never hidden.
 */
function AssignedBand({ row, onDrill }: { row: LedgerRow; onDrill: (t: DrillTarget) => void }): ReactNode {
  return (
    <button
      type="button"
      data-testid="conversion-ledger-assigned-band"
      data-count={row.working}
      onClick={() => onDrill({ segment: `furthest:${row.key}:working`, label: 'Assigned · won clients' })}
      className="w-full text-left rounded-card bg-tick-affirm px-3 py-2.5 hover:brightness-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
    >
      <div className="flex items-baseline gap-2 text-[13px]">
        <Dot tone="won" />
        <span className="font-semibold text-affirm-deep">{row.label} · won clients</span>
        <span className="ml-auto tabular-nums font-semibold text-affirm-deep">{row.working}</span>
      </div>
    </button>
  );
}

/** A clickable population chip in the reconciliation strip — opens the drill. */
function DrillChip({
  testid,
  tone,
  count,
  label,
  onClick,
}: {
  testid: string;
  tone: 'moved' | 'won' | 'loss' | 'deciding' | 'neutral';
  count: number;
  label: string;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-tight px-1 py-0.5 -mx-0.5 hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
    >
      <Dot tone={tone} />
      <span className="tabular-nums text-ink font-semibold">{count}</span>
      <span className="text-sage-400">{label}</span>
    </button>
  );
}

function RateCard({ title, value, caption, testid }: { title: string; value: string; caption: string; testid: string }): ReactNode {
  return (
    <div className="bg-paper rounded-card shadow-sh2 px-5 py-4" data-testid={testid}>
      <div className="text-[12.5px] font-semibold text-sage-400">{title}</div>
      <div className="text-4xl font-display font-semibold text-sage-800 mt-1" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-[12.5px] text-sage-400 mt-0.5">{caption}</div>
    </div>
  );
}

const DISPOSITION_LABEL: Record<ConversionDrillRowWire['disposition'], { text: string; cls: string }> = {
  converted: { text: 'Won', cls: 'text-affirm-deep bg-tick-affirm' },
  closed_unconverted: { text: 'Lost', cls: 'text-pepper bg-pepper/5' },
  in_progress: { text: 'Still deciding', cls: 'text-sage-400 bg-sage-50' },
};

/**
 * The drill panel — a modal list of the actual people behind the clicked
 * number. Its count is server-guaranteed to equal the clicked metric (the
 * trust contract); each row opens the intake drawer (the same path the board
 * uses) when the host passes onOpen (capability-driven). Read-only.
 */
function DrillPanel({
  slug,
  range,
  target,
  onClose,
  onOpen,
}: {
  slug: string;
  range: DateRange;
  target: DrillTarget;
  onClose: () => void;
  onOpen?: (id: string) => void;
}): ReactNode {
  const { drill, isLoading, isError, error, refetch } = useConversionDrill(slug, {
    from: range.from,
    to: range.to,
    segment: target.segment,
  });

  return (
    <OverlayPortal>
      <div
        className="fixed inset-0 z-30 flex items-center justify-center bg-sage-800/20 p-4"
        role="dialog"
        aria-modal="true"
        aria-label={`People: ${target.label}`}
        data-testid="conversion-drill-panel"
        onClick={onClose}
      >
        <div
          className="bg-paper rounded-card shadow-sh2 w-full max-w-md max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-baseline justify-between gap-3 px-5 py-3.5 border-b border-sage-800/[8%]">
            <div>
              <h2 className="font-display text-base text-sage-800">{target.label}</h2>
              {drill && (
                <p className="text-[12px] text-sage-400 mt-0.5" data-testid="conversion-drill-count">
                  {drill.count} {drill.count === 1 ? 'person' : 'people'}
                </p>
              )}
            </div>
            <button
              type="button"
              data-testid="conversion-drill-close"
              onClick={onClose}
              className="text-sage-400 hover:text-sage-800 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight px-1"
            >
              Close
            </button>
          </div>
          <div className="overflow-y-auto px-2 py-2">
            {isLoading ? (
              <div className="px-3"><SkeletonRow /><SkeletonRow /></div>
            ) : isError ? (
              <div className="px-3 py-2"><ErrorBanner error={error} onRetry={refetch} what="the ledger" /></div>
            ) : !drill || drill.rows.length === 0 ? (
              <p className="text-sm text-ink-soft px-3 py-4" data-testid="conversion-drill-empty">This segment is empty for these dates.</p>
            ) : (
              <ul data-testid="conversion-drill-list">
                {drill.rows.map((r) => {
                  const disp = DISPOSITION_LABEL[r.disposition];
                  return (
                    <li key={r.work_item_id}>
                      <button
                        type="button"
                        data-testid="conversion-drill-row"
                        onClick={() => onOpen?.(r.work_item_id)}
                        className="w-full text-left rounded-tight px-3 py-2 hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="text-[13px] font-medium text-ink truncate">{r.display_name ?? 'Unnamed intake'}</span>
                          <span className={`ml-auto flex-shrink-0 text-[10px] font-semibold rounded-pill px-1.5 py-0.5 ${disp.cls}`}>{disp.text}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 text-[11.5px] text-sage-400 mt-0.5">
                          {r.current_stage_label && <span>{r.current_stage_label}</span>}
                          {r.contact_name && <span>· {r.contact_name}</span>}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
